import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { collectionLandingDesktopRevealAnimation } from '@/components/sections/animations/collection-landing-desktop'

import { cn } from '@/lib/utils'

export type CollectionLandingMobileProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    text: string
    items: any[]
  }>

const CollectionLandingMobile = ({
  title,
  text,
  items,
  className,
  ...props
}: CollectionLandingMobileProps) => {
  return (
    <Motion animation={collectionLandingDesktopRevealAnimation}>
      {(motionRef) => (
        <div ref={motionRef}>
          <Section className={cn(className)} {...props}>
            <Container>
              <Grid>
                <div
                  className="relative z-10 col-span-2 flex flex-col"
                  data-collection-landing-sticky
                >
                  <div
                    className="flex w-full flex-col gap-[48px] py-spacing-2xl"
                    data-collection-landing-main-title
                    data-motion-title
                  >
                    {title ? (
                      <div className="flex items-end gap-spacing-2xs">
                        <svg
                          width="8"
                          height="2"
                          viewBox="0 0 8 2"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 1C0 0.447715 0.358172 0 0.8 0H7.2C7.64183 0 8 0.447715 8 1C8 1.55228 7.64183 2 7.2 2H0.8C0.358172 2 0 1.55228 0 1Z"
                          />
                        </svg>

                        <Typography variant="regular" component="h1">
                          {title}
                        </Typography>
                      </div>
                    ) : null}
                    {text ? (
                      <Typography
                        variant="large"
                        paragraph
                        data-collection-landing-text
                        data-motion-text
                        className="text-regular md:text-large text-text-primary-light dark:text-text-primary-dark"
                      >
                        {text}
                      </Typography>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-[48px]">
                    {items?.map((item, index) => (
                      <div key={index} className="" data-motion-image>
                        <div className="relative z-10">
                          <CtaButton
                            href={item?.link || '#'}
                            variant="link"
                            typography="h3"
                            iconSize="24px"
                            className="border-b-[2px] border-b-[rgba(0,0,0,.2)] py-spacing-xs [&_svg]:w-[16px] [&_svg]:translate-y-[2px]"
                          >
                            {item?.title}
                          </CtaButton>
                          <div className="pointer-events-none absolute right-[3rem] top-[2.4rem] flex rounded-radius-minimal bg-surface-primary-light p-spacing-2xs md:top-[1.8rem] dark:bg-surface-primary-dark">
                            <Typography
                              variant="small"
                              className="condensed text-text-primary-invert-light dark:text-text-primary-invert-dark"
                            >
                              {String(item?.items ?? '').padStart(2, '0')}
                            </Typography>
                          </div>
                        </div>

                        <div
                          key={index}
                          className="relative mt-spacing-sm aspect-video"
                          data-collection-landing-image
                        >
                          <Link
                            href={item?.link || '#'}
                            scroll={false}
                            aria-hidden="true"
                            tabIndex={-1}
                          >
                            <img
                              src={item?.image?.url || ''}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover object-center"
                            />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Grid>
            </Container>
          </Section>
        </div>
      )}
    </Motion>
  )
}

export default CollectionLandingMobile
